<template>
  <div v-if="field" class="field-editor-panel">
    <div class="panel-header">
      <h4>{{ t('registration.editField') }}</h4>
      <span class="field-type-badge">{{ typeLabel }}</span>
    </div>

    <div class="editor-content">
      <div class="form-group">
        <label>{{ t('registration.fieldType') }} *</label>
        <select
          v-model="field.type"
          @change="$emit('typeChange')"
          class="field-type-select"
        >
          <option value="text">{{ t('registration.fieldTypes.text') }}</option>
          <option value="email">
            {{ t('registration.fieldTypes.email') }}
          </option>
          <option value="name">{{ t('registration.fieldTypes.name') }}</option>
          <option value="number">
            {{ t('registration.fieldTypes.number') }}
          </option>
          <option value="textarea">
            {{ t('registration.fieldTypes.textarea') }}
          </option>
          <option value="select">
            {{ t('registration.fieldTypes.select') }}
          </option>
          <option value="radio">
            {{ t('registration.fieldTypes.radio') }}
          </option>
          <option value="checkbox">
            {{ t('registration.fieldTypes.checkbox') }}
          </option>
          <option value="date">{{ t('registration.fieldTypes.date') }}</option>
          <option value="time">{{ t('registration.fieldTypes.time') }}</option>
          <option value="section">
            {{ t('registration.fieldTypes.section') }}
          </option>
        </select>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>{{ t('registration.fieldLabel') }} *</label>
          <input
            v-model.lazy="field.label"
            type="text"
            required
            :placeholder="t('registration.fieldLabelPlaceholder')"
          />
        </div>

        <div v-if="field.type !== 'section'" class="form-group">
          <label>{{ t('registration.fieldName') }} *</label>
          <div class="field-name-container">
            <input
              v-model.lazy="field.name"
              type="text"
              required
              :placeholder="t('registration.fieldNamePlaceholder')"
              :disabled="!manuallyEdited"
              @input="$emit('sanitizeName')"
              :class="{
                error: duplicateNames.includes(field.name),
                'auto-generated': !manuallyEdited,
              }"
            />
            <button
              v-if="!manuallyEdited"
              @click.prevent="$emit('enableManual')"
              class="btn btn-secondary btn-sm edit-name-btn"
              type="button"
            >
              <span class="btn-icon">✏️</span>
              Edit
            </button>
            <button
              v-else
              @click.prevent="$emit('disableManual')"
              class="btn btn-outline btn-sm edit-name-btn"
              type="button"
            >
              <span class="btn-icon">🔄</span>
              Auto
            </button>
          </div>
        </div>
      </div>

      <div v-if="field.type !== 'section'" class="form-group">
        <label>{{ t('registration.fieldPlaceholder') }}</label>
        <input
          v-model.lazy="field.placeholder"
          type="text"
          :placeholder="t('registration.fieldPlaceholderPlaceholder')"
        />
      </div>

      <!-- Name field translations (per field) -->
      <div v-if="field.type === 'name'" class="form-row">
        <div class="form-group">
          <label>{{
            t('registration.public.firstName') || 'First Name'
          }}</label>
          <input
            v-model.lazy="field.firstNameLabel"
            type="text"
            :placeholder="t('registration.public.firstName') || 'First Name'"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.public.lastName') || 'Last Name' }}</label>
          <input
            v-model.lazy="field.lastNameLabel"
            type="text"
            :placeholder="t('registration.public.lastName') || 'Last Name'"
          />
        </div>
      </div>

      <!-- Date field translations (per field) -->
      <div v-if="field.type === 'date'" class="form-row">
        <div class="form-group">
          <label>{{ t('registration.dayLabelText') }}</label>
          <input
            v-model.lazy="field.dayLabel"
            type="text"
            :placeholder="t('registration.dayLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.dayPlaceholderText') }}</label>
          <input
            v-model.lazy="field.dayPlaceholder"
            type="text"
            :placeholder="t('registration.dayPlaceholderPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.monthLabelText') }}</label>
          <input
            v-model.lazy="field.monthLabel"
            type="text"
            :placeholder="t('registration.monthLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.monthPlaceholderText') }}</label>
          <input
            v-model.lazy="field.monthPlaceholder"
            type="text"
            :placeholder="t('registration.monthPlaceholderPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.yearLabelText') }}</label>
          <input
            v-model.lazy="field.yearLabel"
            type="text"
            :placeholder="t('registration.yearLabelPlaceholder')"
          />
        </div>
        <div class="form-group">
          <label>{{ t('registration.yearPlaceholderText') }}</label>
          <input
            v-model.lazy="field.yearPlaceholder"
            type="text"
            :placeholder="t('registration.yearPlaceholderPlaceholder')"
          />
        </div>
      </div>

      <div v-if="field.type === 'date'" class="form-group">
        <label>{{ t('registration.monthNames') }}</label>
        <div class="month-names-grid">
          <div
            v-for="(m, idx) in 12"
            :key="'month-name-' + idx"
            class="month-name-item"
          >
            <small>{{ t('registration.monthN', { n: idx + 1 }) }}</small>
            <input
              :value="monthNamesSafe[idx]"
              @input="setMonthName(idx, $event.target.value)"
              type="text"
              :placeholder="defaultMonthNames[idx]"
            />
          </div>
        </div>
      </div>

      <div v-if="field.type !== 'section'" class="form-group inline">
        <label
          ><input v-model="field.required" type="checkbox" />
          {{ t('registration.fieldRequired') }}</label
        >
      </div>

      <div
        v-if="['select', 'radio', 'checkbox'].includes(field.type)"
        class="form-group"
      >
        <label>{{ t('registration.fieldOptions') }}</label>
        <div class="options-list">
          <div
            v-for="(option, optionIndex) in field.options"
            :key="optionIndex"
            class="option-item"
          >
            <input
              v-model.lazy="field.options[optionIndex]"
              type="text"
              :placeholder="`Option ${optionIndex + 1}`"
            />
            <button
              @click.prevent="$emit('removeOption', optionIndex)"
              class="btn-icon btn-danger"
              title="Remove option"
            >
              ✕
            </button>
          </div>
          <button
            @click.prevent="$emit('addOption')"
            class="btn btn-secondary btn-sm"
          >
            {{ t('registration.addOption') }}
          </button>
        </div>
      </div>

      <div v-if="hasAnyValidationControls" class="form-group">
        <label>{{ t('registration.validation') }}</label>
        <div class="validation-grid">
          <div
            v-if="['text', 'textarea'].includes(field.type)"
            class="validation-item"
          >
            <label>{{ t('registration.minLength') }}</label>
            <input
              :value="field.validation && field.validation.minLength"
              @input="updateValidation('minLength', $event.target.value)"
              type="number"
              placeholder="Min"
            />
          </div>
          <div
            v-if="['text', 'textarea'].includes(field.type)"
            class="validation-item"
          >
            <label>{{ t('registration.maxLength') }}</label>
            <input
              :value="field.validation && field.validation.maxLength"
              @input="updateValidation('maxLength', $event.target.value)"
              type="number"
              placeholder="Max"
            />
          </div>
          <div v-if="field.type === 'number'" class="validation-item">
            <label>{{ t('registration.minValue') }}</label>
            <input
              :value="field.validation && field.validation.min"
              @input="updateValidation('min', $event.target.value)"
              type="number"
              placeholder="Min"
            />
          </div>
          <div v-if="field.type === 'number'" class="validation-item">
            <label>{{ t('registration.maxValue') }}</label>
            <input
              :value="field.validation && field.validation.max"
              @input="updateValidation('max', $event.target.value)"
              type="number"
              placeholder="Max"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'FieldEditor',
  props: {
    field: { type: Object, required: false },
    manuallyEdited: { type: Boolean, required: true },
    duplicateNames: { type: Array, required: true },
  },
  emits: [
    'typeChange',
    'sanitizeName',
    'enableManual',
    'disableManual',
    'addOption',
    'removeOption',
  ],
  setup(props, { emit }) {
    const { t } = useI18n()
    const defaultMonthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const ensureDateFieldDefaults = (field) => {
      if (!field || field.type !== 'date') return
      if (!Array.isArray(field.monthNames) || field.monthNames.length !== 12) {
        field.monthNames = [...defaultMonthNames]
      }
    }
    const monthNamesSafe = computed(() => {
      const arr =
        props.field && Array.isArray(props.field.monthNames)
          ? props.field.monthNames
          : null
      if (arr && arr.length === 12) return arr
      // Fallback to 12 empty strings to avoid template errors during init
      return Array.from({ length: 12 }, () => '')
    })
    const setMonthName = (index, value) => {
      if (!props.field) return
      if (
        !Array.isArray(props.field.monthNames) ||
        props.field.monthNames.length !== 12
      ) {
        props.field.monthNames = [...defaultMonthNames]
      }
      // Ensure index is within bounds
      if (index >= 0 && index < 12) {
        props.field.monthNames.splice(index, 1, value)
      }
    }
    // initialize and watch for field changes
    ensureDateFieldDefaults(props.field)
    watch(
      () => props.field,
      (f) => ensureDateFieldDefaults(f)
    )
    const typeLabel = computed(() => {
      const m = {
        text: t('registration.fieldTypes.text'),
        email: t('registration.fieldTypes.email'),
        name: t('registration.fieldTypes.name'),
        number: t('registration.fieldTypes.number'),
        textarea: t('registration.fieldTypes.textarea'),
        select: t('registration.fieldTypes.select'),
        radio: t('registration.fieldTypes.radio'),
        checkbox: t('registration.fieldTypes.checkbox'),
        date: t('registration.fieldTypes.date'),
        time: t('registration.fieldTypes.time'),
        section: t('registration.fieldTypes.section'),
      }
      return props.field ? m[props.field.type] || props.field.type : ''
    })
    const updateValidation = (key, value) => {
      if (!props.field) return
      if (
        !props.field.validation ||
        typeof props.field.validation !== 'object'
      ) {
        props.field.validation = {}
      }
      const num = Number(value)
      props.field.validation[key] = isNaN(num) ? undefined : num
    }
    const hasAnyValidationControls = computed(() => {
      const type = props.field && props.field.type
      return type === 'number' || ['text', 'textarea'].includes(type)
    })

    return {
      t,
      typeLabel,
      defaultMonthNames,
      updateValidation,
      monthNamesSafe,
      setMonthName,
      hasAnyValidationControls,
    }
  },
}
</script>

<style scoped>
.field-editor-panel {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 600;
}
.field-type-badge {
  background: #3498db;
  color: #fff;
  padding: 0.2rem 0.55rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.editor-content {
  padding: 1rem;
  flex: 1 1 auto;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.month-names-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.month-name-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.month-name-item small {
  color: #6c757d;
  font-size: 0.72rem;
}

.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.375rem;
  font-size: 0.9rem;
}
.form-group.inline {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}
.form-group.inline label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0;
  cursor: pointer;
}
.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.875rem;
  box-sizing: border-box;
}
.form-group.inline input[type='checkbox'] {
  width: auto;
  padding: 0;
  margin: 0;
}
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.15rem rgba(52, 152, 219, 0.2);
}

.form-group input.error {
  border-color: #e74c3c;
  box-shadow: 0 0 0 0.15rem rgba(231, 76, 60, 0.2);
}

.form-group input.auto-generated {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.field-name-container {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.field-name-container input {
  flex: 1;
  min-width: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.option-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.option-item input {
  flex: 1;
}
.validation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.validation-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.validation-item label {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
}

.btn {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-outline {
  background: transparent;
  border: 1px solid #6c757d;
  color: #6c757d;
}

.btn-outline:hover {
  background: #6c757d;
  color: #fff;
}

.btn-sm {
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
}

.options-list .btn-sm {
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  width: auto;
  align-self: flex-start;
}

.edit-name-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 3px;
  font-size: 0.75rem;
  color: #6c757d;
}
.btn-danger {
  color: #dc3545;
}

@media (max-width: 900px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .month-names-grid {
    grid-template-columns: 1fr;
  }
}
</style>
